import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PlaylistContext {
  recentTracks?: any[];
  userGenres?: string[];
  timeOfDay?: string;
  mood?: string;
  activity?: string;
  listeningHistory?: any[];
  currentFilters?: any;
  type?: string;
  originalTranscript?: string;
  confidence?: number;
}

export interface UserPreferences {
  preferredGenres?: string[];
  avoidGenres?: string[];
  preferredDecades?: number[];
  energyLevel?: number;
  popularity?: { min?: number; max?: number };
  explicitContent?: boolean;
}

export interface SmartPlaylistRequest {
  prompt: string;
  context?: PlaylistContext;
  preferences?: UserPreferences;
}

// New unified interfaces for the single endpoint
export interface GenerationContext {
  type: 'similar_playlist' | 'similar_song' | 'user_input' | 'quick_play' | 'replace_tracks';

  // Context data based on type
  songs?: Array<{title: string, artist: string}>;
  song?: {title: string, artist: string};
  userRequest?: string;
  genre?: string;
  tracks?: Array<{title: string, artist: string}>;

  // Additional options
  playlistName?: string;
  playlistDescription?: string;
  makePublic?: boolean;
  createPlaylist?: boolean;
  excludeCurrentSongs?: boolean;
  moods?: string[];
  playlistId?: string;
  createNew?: boolean;
}

export interface UnifiedPlaylistResponse {
  success: boolean;
  generatedPlaylist: {
    name: string;
    description: string;
    tracks: Array<{
      name: string;
      artist: string;
      spotify_id?: string;
      preview_url?: string;
    }>;
  };
  spotifyPlaylist?: {
    id: string;
    external_urls: { spotify: string };
  };
  error?: string;
}

// Legacy interfaces kept for backward compatibility
export interface QuotaStatus {
  used: number;
  remaining: number;
  resetTime: Date;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
}

export interface PlaylistSuggestion {
  name: string;
  description: string;
  tracks: Array<{
    name: string;
    artist: string;
    album?: string;
    reasoning?: string;
  }>;
  reasoning: string;
}

export interface ChatResponse {
  message: string;
  suggestions?: string[];
}

export interface PlaylistIntent {
  prompt: string;
  context: {
    type: string;
    originalTranscript: string;
    confidence: number;
  };
  name?: string;
  trackCount?: number;
  genre?: string;
  mood?: string;
  artist?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AIService {
  private chatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  public chatHistory$ = this.chatHistorySubject.asObservable();

  private isGeneratingSubject = new BehaviorSubject<boolean>(false);
  public isGenerating$ = this.isGeneratingSubject.asObservable();

  constructor(private http: HttpClient) {}

  // UNIFIED PLAYLIST GENERATION METHOD
  async generatePlaylist(context: GenerationContext): Promise<UnifiedPlaylistResponse> {
    this.isGeneratingSubject.next(true);
    
    try {
      const response = await this.http.post<UnifiedPlaylistResponse>(
        `${environment.apiBaseUrl}/playlist/generate`,
        { context },
        { withCredentials: true }
      ).toPromise();
      
      if (!response) {
        throw new Error('No response received from AI service');
      }
      
      return response;
    } catch (error: any) {
      console.error('Unified playlist generation error:', error);
      throw new Error(error.error?.error || 'Failed to generate playlist');
    } finally {
      this.isGeneratingSubject.next(false);
    }
  }

  // LEGACY METHOD - Uses unified endpoint with user_input context
  async chatWithAI(message: string): Promise<ChatResponse> {
    const currentHistory = this.chatHistorySubject.value;
    
    // Add user message to history
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    
    const updatedHistory = [...currentHistory, userMessage];
    this.chatHistorySubject.next(updatedHistory);
    
    try {
      // Use unified playlist endpoint for chat functionality
      const response = await this.generatePlaylist({
        type: 'user_input',
        userRequest: message,
        createPlaylist: false
      });
      
      const chatResponse: ChatResponse = {
        message: response.generatedPlaylist.description || 'I can help you create playlists based on your requests.',
        suggestions: ['Create a playlist based on this', 'Generate more tracks like these']
      };
      
      // Add AI response to history
      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: chatResponse.message,
        timestamp: new Date()
      };
      
      const finalHistory = [...updatedHistory, aiMessage];
      this.chatHistorySubject.next(finalHistory);
      
      return chatResponse;
    } catch (error: any) {
      console.error('AI chat error:', error);
      
      // Remove the user message from history if the request failed
      this.chatHistorySubject.next(currentHistory);
      
      throw new Error(error.error?.error || 'Failed to chat with AI');
    }
  }

  getChatHistory(): ChatMessage[] {
    return this.chatHistorySubject.value;
  }

  clearChatHistory(): void {
    this.chatHistorySubject.next([]);
  }

  getContextualPlaylistPrompts(timeOfDay?: string, mood?: string): string[] {
    const prompts: string[] = [
      'Create a workout playlist with high-energy tracks',
      'Generate a chill evening playlist for relaxation',
      'Make a focus playlist for studying or working',
      'Create a party playlist with dance hits',
      'Generate a road trip playlist with classic hits'
    ];

    // Add time-based suggestions
    if (timeOfDay) {
      switch (timeOfDay.toLowerCase()) {
        case 'morning':
          prompts.unshift('Create an energizing morning playlist to start the day');
          break;
        case 'afternoon':
          prompts.unshift('Generate a productive afternoon playlist');
          break;
        case 'evening':
          prompts.unshift('Make a relaxing evening playlist for unwinding');
          break;
        case 'night':
          prompts.unshift('Create a late-night playlist with mellow vibes');
          break;
      }
    }

    // Add mood-based suggestions
    if (mood) {
      prompts.unshift(`Generate a ${mood} playlist that matches my current mood`);
    }

    return prompts;
  }

  getSuggestedChatPrompts(): string[] {
    return [
      'What are some popular music trends right now?',
      'Can you recommend artists similar to my favorite bands?',
      'How can I discover new music in my favorite genres?',
      'What makes a good playlist flow?',
      'Can you help me organize my music library?'
    ];
  }

  // LEGACY SMART PLAYLIST METHODS - Now use unified endpoint

  async generateSmartPlaylist(request: SmartPlaylistRequest): Promise<any> {
    const response = await this.generatePlaylist({
      type: 'user_input',
      userRequest: request.prompt,
      createPlaylist: false
    });
    
    // Convert to legacy format for backward compatibility
    return {
      id: `playlist_${Date.now()}`,
      name: response.generatedPlaylist.name,
      description: response.generatedPlaylist.description,
      tracks: response.generatedPlaylist.tracks,
      metadata: {
        strategy: null,
        filteringStats: {
          totalFound: response.generatedPlaylist.tracks.length,
          filtered: 0,
          replaced: 0,
          final: response.generatedPlaylist.tracks.length
        },
        processingTime: 0
      }
    };
  }

  async createSpotifyPlaylist(generatedPlaylist: any, makePublic: boolean = false): Promise<any> {
    // Convert generated playlist to tracks format and use unified endpoint
    const context: GenerationContext = {
      type: 'user_input',
      userRequest: `Create playlist: ${generatedPlaylist.name}`,
      createPlaylist: true,
      playlistName: 'Dynamic Playlist',
      playlistDescription: generatedPlaylist.description,
      makePublic
    };
    
    const response = await this.generatePlaylist(context);
    
    if (!response.spotifyPlaylist) {
      throw new Error('Failed to create playlist on Spotify');
    }
    
    return response.spotifyPlaylist;
  }

  async generateAndCreatePlaylist(
    request: SmartPlaylistRequest, 
    makePublic: boolean = false,
    playlistName?: string,
    playlistDescription?: string
  ): Promise<{ playlist: any; generatedPlaylist: any }> {
    const response = await this.generatePlaylist({
      type: 'user_input',
      userRequest: request.prompt,
      createPlaylist: true,
      playlistName: playlistName || 'Dynamic Playlist',
      playlistDescription,
      makePublic
    });
    
    const legacyFormat = {
      id: `playlist_${Date.now()}`,
      name: response.generatedPlaylist.name,
      description: response.generatedPlaylist.description,
      tracks: response.generatedPlaylist.tracks,
      metadata: {
        strategy: null,
        filteringStats: {
          totalFound: response.generatedPlaylist.tracks.length,
          filtered: 0,
          replaced: 0,
          final: response.generatedPlaylist.tracks.length
        },
        processingTime: 0
      }
    };
    
    return {
      playlist: response.spotifyPlaylist,
      generatedPlaylist: legacyFormat
    };
  }

  getSmartPlaylistPrompts(): string[] {
    return [
      'Create an energetic workout playlist with electronic and rock music',
      'Generate a chill study playlist with lo-fi and ambient tracks',
      'Make a nostalgic 90s playlist with alternative and grunge hits',
      'Create a romantic dinner playlist with jazz and soul music',
      'Generate a road trip playlist with classic rock and indie favorites',
      'Make a morning motivation playlist with uplifting pop and hip-hop',
      'Create a late-night vibes playlist with R&B and downtempo',
      'Generate a party playlist with current dance and pop hits',
      'Make a focus playlist with instrumental and classical music',
      'Create a discovery playlist introducing me to new indie artists'
    ];
  }

  // Legacy quota methods - no longer supported in unified system
  getCurrentQuotaStatus(): QuotaStatus | null {
    return null;
  }

  getActivityBasedPrompts(activity: string): string[] {
    const prompts: { [key: string]: string[] } = {
      'workout': [
        'High-energy workout playlist with electronic beats',
        'Motivational gym playlist with rock and hip-hop',
        'Cardio playlist with fast-paced dance music'
      ],
      'study': [
        'Focus playlist with instrumental and ambient music',
        'Study playlist with lo-fi hip-hop and jazz',
        'Concentration playlist with classical and minimal techno'
      ],
      'work': [
        'Productive work playlist with indie and electronic',
        'Office background music with jazz and folk',
        'Creative work playlist with alternative and experimental'
      ],
      'party': [
        'Party playlist with current hits and dance music',
        'House party playlist with hip-hop and pop',
        'Dance party playlist with electronic and disco'
      ],
      'relaxation': [
        'Chill evening playlist for unwinding',
        'Meditation playlist with ambient and new age',
        'Spa relaxation playlist with nature sounds and soft music'
      ]
    };
    
    return prompts[activity.toLowerCase()] || [];
  }

  getMoodBasedPrompts(mood: string): string[] {
    const prompts: { [key: string]: string[] } = {
      'happy': [
        'Uplifting playlist to boost my happy mood',
        'Feel-good music with pop and indie hits',
        'Joyful playlist with upbeat and cheerful songs'
      ],
      'sad': [
        'Melancholic playlist for when I\'m feeling down',
        'Sad songs that match my emotional state',
        'Contemplative playlist with indie and folk'
      ],
      'energetic': [
        'High-energy playlist to pump me up',
        'Energetic music with rock and electronic',
        'Motivational playlist with powerful vocals'
      ],
      'calm': [
        'Peaceful playlist for relaxation',
        'Calm music with acoustic and ambient',
        'Soothing playlist for stress relief'
      ],
      'nostalgic': [
        'Nostalgic playlist with throwback hits',
        'Memory lane playlist from my favorite era',
        'Vintage music that brings back memories'
      ]
    };
    
    return prompts[mood.toLowerCase()] || [];
  }

  // TRACK REPLACEMENT METHODS - Now use unified endpoint

  async replaceTracksWithAI(
    tracks: any[], 
    context?: PlaylistContext, 
    playlistContext?: any
  ): Promise<{ replacements: any[]; requestedCount: number; foundCount: number }> {
    const trackList = tracks.map(t => ({ title: t.name, artist: t.artists?.[0]?.name || t.artist }));
    
    const response = await this.generatePlaylist({
      type: 'replace_tracks',
      tracks: trackList,
      createPlaylist: false
    });
    
    return {
      replacements: response.generatedPlaylist.tracks,
      requestedCount: tracks.length,
      foundCount: response.generatedPlaylist.tracks.length
    };
  }

  async getSimilarTracks(
    track: any, 
    context?: PlaylistContext, 
    count: number = 3
  ): Promise<{ original: any; alternatives: any[]; count: number }> {
    const response = await this.generatePlaylist({
      type: 'similar_song',
      song: { title: track.name, artist: track.artists?.[0]?.name || track.artist },
      createPlaylist: false
    });
    
    // Take only the requested number of tracks
    const alternatives = response.generatedPlaylist.tracks.slice(0, count);
    
    return {
      original: track,
      alternatives,
      count: alternatives.length
    };
  }

  async extractPlaylistIntent(transcript: string): Promise<PlaylistIntent> {
    // Use unified endpoint to process voice commands as user input
    try {
      const response = await this.generatePlaylist({
        type: 'user_input',
        userRequest: transcript,
        createPlaylist: false
      });
      
      return {
        prompt: `Create a playlist: ${response.generatedPlaylist.name}`,
        context: {
          type: 'voice_command',
          originalTranscript: transcript,
          confidence: 0.8
        },
        name: response.generatedPlaylist.name,
        trackCount: response.generatedPlaylist.tracks.length
      };
    } catch (error) {
      // Fallback: create a basic intent locally
      return this.createFallbackIntent(transcript);
    }
  }

  private createFallbackIntent(transcript: string): PlaylistIntent {
    // Simple fallback intent extraction
    const lowerTranscript = transcript.toLowerCase();
    
    let genre: string | undefined;
    let mood: string | undefined;
    let artist: string | undefined;
    
    // Basic genre detection
    const genres = ['rock', 'pop', 'hip-hop', 'electronic', 'jazz', 'classical', 'country', 'indie', 'folk', 'metal'];
    genre = genres.find(g => lowerTranscript.includes(g));
    
    // Basic mood detection
    const moods = ['happy', 'sad', 'energetic', 'calm', 'upbeat', 'chill', 'relaxing'];
    mood = moods.find(m => lowerTranscript.includes(m));
    
    // Basic artist detection (look for "by" or "from")
    const artistMatch = transcript.match(/(?:by|from)\s+([a-zA-Z\s]+?)(?:\s|$|,)/i);
    if (artistMatch) {
      artist = artistMatch[1].trim();
    }
    
    // Create refined prompt
    let prompt = `Create a playlist based on: ${transcript}`;
    if (genre) prompt += ` with ${genre} music`;
    if (mood) prompt += ` with ${mood} vibes`;
    if (artist) prompt += ` similar to ${artist}`;
    
    return {
      prompt,
      context: {
        type: 'voice_command',
        originalTranscript: transcript,
        confidence: 0.7 // Lower confidence for fallback
      },
      name: transcript.length > 30 ? transcript.substring(0, 30) + '...' : transcript,
      trackCount: 20,
      genre,
      mood,
      artist
    };
  }
}